/**
 * Glossary internal cross-link map.
 *
 * Every glossary term links out to the commercial pages (services), the
 * geo pages (locations) and the editorial pages (blog posts / pillar guides)
 * that are topically closest to it. This keeps informational glossary traffic
 * flowing into commercial-intent destinations instead of dead-ending, and
 * strengthens the topical clusters those destination pages sit in.
 */

export interface GlossaryCrossLinks {
  /** Service slugs -> /services/<slug>/ */
  services: string[];
  /** Location slugs -> /locations/<slug>/ */
  locations: string[];
  /** Editorial paths, absolute site-relative (blog posts live at root, guides under /guides/). */
  topics: string[];
}

const G = (slug: string) => `/guides/${slug}/`;
const P = (slug: string) => `/${slug}/`;

const AI_TOPICS = [P("how-to-rank-in-ai-overviews"), P("ai-search-optimization-guide"), G("aeo-geo-playbook")];
const LOCAL_TOPICS = [P("local-seo-in-a-map-pack-that-keeps-shrinking"), G("local-seo-playbook")];
const TECH_TOPICS = [G("technical-seo-audit-checklist"), P("core-web-vitals-2026")];
const CONTENT_TOPICS = [P("topical-authority-and-clusters"), G("the-complete-guide-to-seo-in-2026")];
const SCHEMA_TOPICS = [P("schema-that-actually-moves-the-needle"), G("technical-seo-audit-checklist")];
const LINK_TOPICS = [G("austin-link-building-strategy"), P("topical-authority-and-clusters")];
const DATA_TOPICS = [P("the-analytics-stack-we-actually-use"), G("the-complete-guide-to-seo-in-2026")];

export const GLOSSARY_LINKS: Record<string, GlossaryCrossLinks> = {
  // ---- AI Search ----
  aeo: { services: ["aeo-geo", "content-strategy"], locations: ["austin", "dallas"], topics: AI_TOPICS },
  geo: { services: ["aeo-geo", "content-marketing"], locations: ["austin", "houston"], topics: [...AI_TOPICS, P("geo-success-matrix-explained")] },
  "ai-overviews": { services: ["aeo-geo", "content-strategy"], locations: ["austin", "plano"], topics: [P("ai-overviews-what-changed-in-search-2026"), ...AI_TOPICS.slice(0, 2)] },
  entity: { services: ["aeo-geo", "digital-pr"], locations: ["austin", "round-rock"], topics: [...AI_TOPICS.slice(0, 2), P("schema-that-actually-moves-the-needle")] },

  // ---- Content ----
  "e-e-a-t": { services: ["content-strategy", "digital-pr"], locations: ["austin", "dallas"], topics: CONTENT_TOPICS },
  "topical-authority": { services: ["content-strategy", "content-marketing"], locations: ["austin", "cedar-park"], topics: CONTENT_TOPICS },
  "content-cluster": { services: ["content-marketing", "content-strategy"], locations: ["austin", "georgetown"], topics: CONTENT_TOPICS },

  // ---- On-Page ----
  "internal-linking": { services: ["technical-seo", "content-strategy"], locations: ["austin", "dallas"], topics: [...CONTENT_TOPICS.slice(0, 1), G("technical-seo-audit-checklist")] },
  "meta-description": { services: ["content-strategy", "cro"], locations: ["austin", "plano"], topics: [G("the-complete-guide-to-seo-in-2026"), P("topical-authority-and-clusters")] },
  "title-tag": { services: ["content-strategy", "cro"], locations: ["austin", "dallas"], topics: [G("the-complete-guide-to-seo-in-2026"), P("topical-authority-and-clusters")] },
  h1: { services: ["web-design", "content-strategy"], locations: ["austin", "round-rock"], topics: [G("the-complete-guide-to-seo-in-2026"), G("technical-seo-audit-checklist")] },
  "alt-text": { services: ["web-design", "technical-seo"], locations: ["austin", "cedar-park"], topics: TECH_TOPICS },
  "featured-snippet": { services: ["content-strategy", "aeo-geo"], locations: ["austin", "georgetown"], topics: [...AI_TOPICS.slice(0, 1), ...CONTENT_TOPICS.slice(0, 1)] },

  // ---- Technical SEO ----
  "core-web-vitals": { services: ["technical-seo", "web-design"], locations: ["austin", "dallas"], topics: TECH_TOPICS },
  inp: { services: ["technical-seo", "web-design"], locations: ["austin", "plano"], topics: TECH_TOPICS },
  lcp: { services: ["technical-seo", "web-design"], locations: ["austin", "round-rock"], topics: TECH_TOPICS },
  cls: { services: ["technical-seo", "web-design"], locations: ["austin", "cedar-park"], topics: TECH_TOPICS },
  "crawl-budget": { services: ["technical-seo", "ecommerce-seo"], locations: ["dallas", "houston"], topics: [G("technical-seo-audit-checklist"), P("topical-authority-and-clusters")] },
  canonical: { services: ["technical-seo", "migration-seo"], locations: ["austin", "dallas"], topics: [G("technical-seo-audit-checklist"), P("core-web-vitals-2026")] },
  "robots-txt": { services: ["technical-seo", "migration-seo"], locations: ["austin", "houston"], topics: [G("technical-seo-audit-checklist")] },
  sitemap: { services: ["technical-seo", "ecommerce-seo"], locations: ["austin", "georgetown"], topics: [G("technical-seo-audit-checklist")] },
  schema: { services: ["technical-seo", "aeo-geo"], locations: ["austin", "dallas"], topics: SCHEMA_TOPICS },
  "json-ld": { services: ["technical-seo", "aeo-geo"], locations: ["austin", "plano"], topics: SCHEMA_TOPICS },

  // ---- Off-Page ----
  "anchor-text": { services: ["link-building", "digital-pr"], locations: ["austin", "dallas"], topics: LINK_TOPICS },
  backlink: { services: ["link-building", "digital-pr"], locations: ["austin", "round-rock"], topics: LINK_TOPICS },
  dr: { services: ["link-building", "analytics-reporting"], locations: ["austin", "dallas"], topics: LINK_TOPICS },
  citation: { services: ["local-seo", "google-business-profile"], locations: ["austin", "cedar-park"], topics: LOCAL_TOPICS },

  // ---- Local ----
  nap: { services: ["local-seo", "google-business-profile"], locations: ["austin", "round-rock"], topics: LOCAL_TOPICS },
  gbp: { services: ["google-business-profile", "local-seo"], locations: ["austin", "georgetown"], topics: LOCAL_TOPICS },
  "map-pack": { services: ["local-seo", "google-business-profile"], locations: ["austin", "dallas"], topics: LOCAL_TOPICS },

  // ---- Analytics ----
  serp: { services: ["analytics-reporting", "content-strategy"], locations: ["austin", "dallas"], topics: [...AI_TOPICS.slice(0, 1), ...DATA_TOPICS.slice(0, 1)] },
  ctr: { services: ["cro", "content-strategy"], locations: ["austin", "plano"], topics: DATA_TOPICS },
  impression: { services: ["analytics-reporting", "aeo-geo"], locations: ["austin", "houston"], topics: DATA_TOPICS },
  conversion: { services: ["cro", "analytics-reporting"], locations: ["austin", "dallas"], topics: DATA_TOPICS },
  ga4: { services: ["analytics-reporting", "cro"], locations: ["austin", "round-rock"], topics: DATA_TOPICS },
  "search-console": { services: ["analytics-reporting", "technical-seo"], locations: ["austin", "dallas"], topics: [...DATA_TOPICS.slice(0, 1), G("technical-seo-audit-checklist")] },
};

export function getGlossaryLinks(slug: string): GlossaryCrossLinks {
  return GLOSSARY_LINKS[slug] ?? { services: ["local-seo", "technical-seo"], locations: ["austin", "dallas"], topics: CONTENT_TOPICS };
}
